import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { getCurrentUserProfile, updateCurrentUserProfile, getBadgeAwards, getProfilePictureUrl, getBadgeIconUrl, getBadgeRule, formatApiError, clearTokens, changePassword } from '../services/api';
import { getDateLocale } from '../i18n';
import AdminTopBar from './AdminTopBar';
import ProfilePictureUpload from './ProfilePictureUpload';
import BadgeAwardDisputeModal from './BadgeAwardDisputeModal';
import MFASettings from './MFASettings';
import defaultBadgeIcon from '../assets/badges/default-badge-icon.svg';
import './Login.css';

const ProfilePage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(['profile', 'common']);
  const [userData, setUserData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    role: '',
    role_display: '',
    date_joined: '',
    team: null,
    manager: null,
    profile_picture: null,
    initials: '',
    total_points: 0,
    unread_notification_count: 0
  });
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [badgesLoading, setBadgesLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: ''
  });
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [disputeModalOpen, setDisputeModalOpen] = useState(false);
  const [selectedBadgeAward, setSelectedBadgeAward] = useState(null);
  const [ruleModalOpen, setRuleModalOpen] = useState(false);
  const [selectedRule, setSelectedRule] = useState(null);
  const [ruleLoading, setRuleLoading] = useState(false);

  // Change Password modal (available to all roles: Employee, Manager, Admin)
  const [changePasswordModalOpen, setChangePasswordModalOpen] = useState(false);
  const [changePasswordForm, setChangePasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [changePasswordSaving, setChangePasswordSaving] = useState(false);
  const [changePasswordFieldErrors, setChangePasswordFieldErrors] = useState({});
  const [changePasswordGeneralError, setChangePasswordGeneralError] = useState(null);
  const [changePasswordSuccess, setChangePasswordSuccess] = useState(false);
  const [showChangePasswordFields, setShowChangePasswordFields] = useState({ current: false, new: false, confirm: false });

  // Fetch user data on mount
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getCurrentUserProfile();
        setUserData(data);
        setFormData({
          first_name: data.first_name || '',
          last_name: data.last_name || ''
        });
      } catch (err) {
        console.error('Failed to fetch user profile:', err);
        setError(t('loadFailed'));
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [t]);

  // Close Change Password modal on Escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && changePasswordModalOpen && !changePasswordSaving) {
        closeChangePasswordModal();
      }
    };
    if (changePasswordModalOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [changePasswordModalOpen, changePasswordSaving]);

  // Fetch badges
  useEffect(() => {
    const fetchBadges = async () => {
      try {
        setBadgesLoading(true);
        const badgesData = await getBadgeAwards();
        setBadges(badgesData.results || badgesData || []);
      } catch (err) {
        console.error('Failed to fetch badges:', err);
      } finally {
        setBadgesLoading(false);
      }
    };

    fetchBadges();
  }, []);

  const refreshBadges = async () => {
    try {
      const badgesData = await getBadgeAwards();
      setBadges(badgesData.results || badgesData || []);
      } catch (err) {
      console.error('Failed to refresh badges:', err);
    }
  };

  const handleOpenDisputeModal = (badgeAward) => {
    setSelectedBadgeAward(badgeAward);
    setDisputeModalOpen(true);
  };

  const handleCloseDisputeModal = () => {
    setDisputeModalOpen(false);
    setSelectedBadgeAward(null);
  };

  const handleDisputeSuccess = () => {
    refreshBadges();
  };

  const handleViewRule = async (badgeAward) => {
    if (!badgeAward.badge?.id) return;
    
    setRuleLoading(true);
    setRuleModalOpen(true);
    try {
      const rule = await getBadgeRule(badgeAward.badge.id);
      setSelectedRule(rule);
      } catch (err) {
      console.error('Failed to fetch badge rule:', err);
      setError(t('loadRuleFailed'));
      } finally {
      setRuleLoading(false);
    }
  };

  const handleCloseRuleModal = () => {
    setRuleModalOpen(false);
    setSelectedRule(null);
  };

  const getDisputeStatusBadge = (badge) => {
    if (!badge.dispute_status) return null;
    
    const statusConfig = {
      'pending': { color: 'bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-900/40 dark:text-yellow-200 dark:border-yellow-700/70', label: t('disputePending') },
      'under_review': { color: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/40 dark:text-blue-200 dark:border-blue-700/70', label: t('underReview') },
      'resolved_upheld': { color: 'bg-green-50 text-green-700 border-green-200 dark:bg-green-900/40 dark:text-green-200 dark:border-green-700/70', label: t('resolvedUpheld') },
      'resolved_dismissed': { color: 'bg-gray-50 text-gray-700 border-gray-200 dark:bg-slate-700/60 dark:text-slate-200 dark:border-slate-600/80', label: t('resolvedDismissed') },
    };
    
    const config = statusConfig[badge.dispute_status] || statusConfig['pending'];
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const roleLabels = {
    'EMPLOYE': t('roleEmployee'),
    'MANAGER': t('roleManager'),
    'ADMIN': t('roleAdmin')
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString(getDateLocale(), {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatMinHoursHHMM = (value) => {
    if (value === null || value === undefined || value === '') return '';
    const num = parseFloat(value);
    if (isNaN(num) || num < 0) return String(value);
    if (num === 0) return '0:00';
    const h = Math.floor(num);
    const m = Math.round((num % 1) * 60);
    return `${h}:${m.toString().padStart(2, '0')}`;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
    // Clear success message when user makes changes
    if (success) {
      setSuccess(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(false);
    setFieldErrors({});

    try {
      const updatedData = await updateCurrentUserProfile({
        first_name: formData.first_name,
        last_name: formData.last_name
      });
      
      // Update user data with response
      setUserData(updatedData);
      setSuccess(true);
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess(false);
      }, 3000);
    } catch (err) {
      console.error('Failed to update profile:', err);
      const formattedError = formatApiError(err);
      setError(formattedError.generalError || t('updateFailed'));
      setFieldErrors(formattedError.fieldErrors || {});
    } finally {
      setSaving(false);
    }
  };


  const handleBack = () => {
    const userRole = userData?.role;
    if (userRole === 'EMPLOYE') {
      navigate('/employee-dashboard');
    } else if (userRole === 'MANAGER') {
      navigate('/manager-dashboard');
    } else if (userRole === 'ADMIN') {
      navigate('/admin-dashboard');
    } else {
      navigate('/admin-dashboard');
    }
  };

  const handleSignOut = () => {
    clearTokens();
    navigate('/login');
  };

  const openChangePasswordModal = () => {
    setChangePasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setChangePasswordFieldErrors({});
    setChangePasswordGeneralError(null);
    setChangePasswordSuccess(false);
    setShowChangePasswordFields({ current: false, new: false, confirm: false });
    setChangePasswordModalOpen(true);
  };

  const closeChangePasswordModal = () => {
    setChangePasswordModalOpen(false);
    setChangePasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setChangePasswordFieldErrors({});
    setChangePasswordGeneralError(null);
    setChangePasswordSuccess(false);
  };

  const handleChangePasswordInputChange = (field, value) => {
    setChangePasswordForm(prev => ({ ...prev, [field]: value }));
    setChangePasswordFieldErrors(prev => {
      const next = { ...prev };
      delete next[field === 'currentPassword' ? 'old_password' : field === 'newPassword' ? 'new_password' : 'confirm_password'];
      return next;
    });
    setChangePasswordGeneralError(null);
  };

  const validateChangePasswordForm = () => {
    const err = {};
    if (!changePasswordForm.currentPassword.trim()) {
      err.old_password = [t('currentPasswordRequired')];
    }
    if (!changePasswordForm.newPassword) {
      err.new_password = [t('newPasswordRequired')];
    } else if (changePasswordForm.newPassword.length < 8) {
      err.new_password = [t('passwordMinLength')];
    }
    if (changePasswordForm.newPassword !== changePasswordForm.confirmPassword) {
      err.confirm_password = [t('passwordsDoNotMatch')];
    }
    if (!changePasswordForm.confirmPassword) {
      err.confirm_password = [t('confirmPasswordRequired')];
    }
    setChangePasswordFieldErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleChangePasswordSubmit = async (e) => {
    e.preventDefault();
    setChangePasswordGeneralError(null);
    if (!validateChangePasswordForm()) return;
    setChangePasswordSaving(true);
    try {
      await changePassword(
        changePasswordForm.currentPassword,
        changePasswordForm.newPassword,
        changePasswordForm.confirmPassword
      );
      setChangePasswordSuccess(true);
      setTimeout(() => {
        closeChangePasswordModal();
      }, 1500);
    } catch (err) {
      const formatted = formatApiError(err);
      setChangePasswordGeneralError(formatted.generalError || t('changePasswordFailed'));
      setChangePasswordFieldErrors(formatted.fieldErrors || {});
    } finally {
      setChangePasswordSaving(false);
    }
  };

  const getChangePasswordFieldError = (apiKey) => {
    const msg = changePasswordFieldErrors[apiKey];
    return Array.isArray(msg) ? msg[0] : msg || null;
  };

  return (
    <div className=" from-slate-50 to-slate-100 text-text-main overflow-hidden h-screen flex flex-col">
      <main className="flex-1 flex flex-col h-full overflow-hidden relative w-full">
        <AdminTopBar
          title={t('pageTitle')}
          searchPlaceholder=""
          onSearch={() => {}}
          onMenuClick={() => {}}
          onHelpClick={() => {}}
          onSettingsClick={() => {}}
          showSearch={false}
        />
        <div className="flex-1 overflow-y-auto p-6 scroll-smooth">
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ 
              duration: 0.3,
              ease: [0.4, 0, 0.2, 1]
            }}
            className="max-w-[1400px] mx-auto flex flex-col gap-8"
          >
            {/* Header - match example */}
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100">{t('settingsTitle')}</h1>
                <p className="text-slate-600 dark:text-slate-400 mt-1">{t('settingsSubtitle')}</p>
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={openChangePasswordModal}
                  className="inline-flex items-center justify-center gap-2 rounded-md h-9 px-3 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-sm font-medium border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                >
                  <span className="material-symbols-outlined text-[18px]">lock</span>
                  {t('changePassword')}
                </button>
                <button
                  type="button"
                  onClick={handleSignOut}
                  className="inline-flex items-center justify-center gap-2 rounded-md h-9 px-3 bg-white text-slate-700 text-sm font-medium border border-slate-300 hover:bg-slate-50 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-600 dark:hover:bg-slate-700 transition-colors"
                >
                  <span className="material-symbols-outlined text-[18px]">logout</span>
                  {t('signOut')}
                </button>
                {(!loading && userData?.role === 'EMPLOYE' && (!userData?.team || userData?.team === null)) ? null : (
                  <button
                    onClick={handleBack}
                    className="inline-flex items-center justify-center gap-2 rounded-md h-9 px-3 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-sm font-medium border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                  >
                    <span className="material-symbols-outlined text-[18px]">arrow_back</span>
                    {t('backToDashboard')}
                  </button>
                )}
              </div>
            </div>

            {/* Success Message */}
            {success && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-emerald-50 border border-emerald-200 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700/50 dark:text-emerald-200 px-4 py-3 rounded-lg flex items-center gap-2"
              >
                <span className="material-symbols-outlined text-[20px]">check_circle</span>
                <span className="text-sm font-medium">{t('profileUpdated')}</span>
              </motion.div>
            )}

            {/* Error Message */}
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-red-50 border border-red-200 text-red-700 dark:bg-red-900/30 dark:border-red-700/50 dark:text-red-200 px-4 py-3 rounded-lg flex items-center gap-2"
              >
                <span className="material-symbols-outlined text-[20px]">error</span>
                <span className="text-sm font-medium">{error}</span>
              </motion.div>
            )}

            {/* Unassigned team message for employees */}
            {!loading && userData?.role === 'EMPLOYE' && (!userData.team || userData.team === null) && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-amber-50 border border-amber-200 text-amber-800 dark:bg-amber-900/30 dark:border-amber-700/50 dark:text-amber-200 px-4 py-4 rounded-lg flex items-start gap-3"
              >
                <span className="material-symbols-outlined text-[24px] text-amber-600 dark:text-amber-400 shrink-0 mt-0.5">info</span>
                <div>
                  <p className="text-sm font-semibold text-amber-900 dark:text-amber-100">{t('notAssignedTitle')}</p>
                  <p className="text-sm text-amber-800 dark:text-amber-200 mt-1">
                    {t('notAssignedBody')}
                  </p>
                </div>
              </motion.div>
            )}

            {/* Loading State */}
            {loading ? (
              <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-600 p-12 flex items-center justify-center">
                <div className="text-slate-500 dark:text-slate-400">{t('loadingProfile')}</div>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
                {/* Left Column - Profile and Personal Information (same layout for all roles) */}
                <div className="lg:col-span-3 space-y-6">
                  {/* Single Card: Profile Picture + Personal Information */}
                  <div className="p-6 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-600 shadow-sm">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      {/* Profile Picture Section - match example left column */}
                      <div className="flex flex-col items-center justify-center">
                        <ProfilePictureUpload
                          layout="inline"
                          currentPictureUrl={getProfilePictureUrl(userData.profile_picture)}
                          initials={userData.initials || ''}
                          onUpdate={(updatedData) => setUserData(updatedData)}
                        />
                      </div>

                      {/* Personal Information Section - match example right 2 cols */}
                      <div className="md:col-span-2">
                        <h3 className="font-semibold text-slate-900 dark:text-slate-100 mb-4">{t('personalInformation')}</h3>
                        <form onSubmit={handleSubmit} id="profile-form">
                          <div className="space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="text-sm font-medium text-slate-700 dark:text-slate-300">{t('firstName')}</label>
                                <input
                                  type="text"
                                  name="first_name"
                                  value={formData.first_name}
                                  onChange={handleInputChange}
                                  className={`mt-1 w-full h-9 rounded-md border px-3 text-sm ${
                                    fieldErrors.first_name ? 'border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/30' : 'border-slate-200 bg-white dark:border-slate-600 dark:bg-slate-800'
                                  } focus:outline-none focus:ring-1 focus:ring-slate-300`}
                                  placeholder={t('firstNamePlaceholder')}
                                  disabled={saving}
                                />
                                {fieldErrors.first_name && (
                                  <p className="text-red-600 text-xs mt-1">{fieldErrors.first_name[0]}</p>
                                )}
                              </div>
                              <div>
                                <label className="text-sm font-medium text-slate-700 dark:text-slate-300">{t('lastName')}</label>
                                <input
                                  type="text"
                                  name="last_name"
                                  value={formData.last_name}
                                  onChange={handleInputChange}
                                  className={`mt-1 w-full h-9 rounded-md border px-3 text-sm ${
                                    fieldErrors.last_name ? 'border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/30' : 'border-slate-200 bg-white dark:border-slate-600 dark:bg-slate-800'
                                  } focus:outline-none focus:ring-1 focus:ring-slate-300`}
                                  placeholder={t('lastNamePlaceholder')}
                                  disabled={saving}
                                />
                                {fieldErrors.last_name && (
                                  <p className="text-red-600 text-xs mt-1">{fieldErrors.last_name[0]}</p>
                                )}
                              </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="text-sm font-medium text-slate-700 dark:text-slate-300">{t('email')}</label>
                                <input
                                  type="email"
                                  value={userData.email || ''}
                                  disabled
                                  className="mt-1 w-full h-9 rounded-md border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 px-3 text-sm text-slate-600 dark:text-slate-300"
                                />
                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{t('emailCannotChange')}</p>
                              </div>
                              <div>
                                <label className="text-sm font-medium text-slate-700 dark:text-slate-300">{t('role')}</label>
                                <input
                                  type="text"
                                  value={roleLabels[userData.role] || userData.role || ''}
                                  disabled
                                  className="mt-1 w-full h-9 rounded-md border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 px-3 text-sm text-slate-600 dark:text-slate-300"
                                />
                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{t('roleCannotChange')}</p>
                              </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                              {userData.team ? (
                                <div>
                                  <label className="text-sm font-medium text-slate-700 dark:text-slate-300">{t('team')}</label>
                                  <input
                                    type="text"
                                    value={userData.team.name || ''}
                                    disabled
                                    className="mt-1 w-full h-9 rounded-md border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 px-3 text-sm text-slate-600 dark:text-slate-300"
                                  />
                                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{t('teamCannotChange')}</p>
                                </div>
                              ) : (
                                <div />
                              )}
                              <div>
                                <label className="text-sm font-medium text-slate-700 dark:text-slate-300">{t('dateJoined')}</label>
                                <input
                                  type="text"
                                  value={formatDate(userData.date_joined)}
                                  disabled
                                  className="mt-1 w-full h-9 rounded-md border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 px-3 text-sm text-slate-600 dark:text-slate-300"
                                />
                              </div>
                            </div>

                            {userData.manager && (
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <label className="text-sm font-medium text-slate-700 dark:text-slate-300">{t('manager')}</label>
                                  <input
                                    type="text"
                                    value={
                                      userData.manager.first_name || userData.manager.last_name
                                        ? `${userData.manager.first_name || ''} ${userData.manager.last_name || ''}`.trim()
                                        : userData.manager.email || ''
                                    }
                                    disabled
                                    className="mt-1 w-full h-9 rounded-md border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 px-3 text-sm text-slate-600 dark:text-slate-300"
                                  />
                                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{t('managerCannotChange')}</p>
                                </div>
                              </div>
                            )}
                          </div>
                        </form>
                      </div>
                    </div>

                    {/* Action Buttons - match example border-t border-slate-200 */}
                    <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-slate-200 dark:border-slate-600">
                      {!(userData?.role === 'EMPLOYE' && (!userData?.team || userData?.team === null)) && (
                        <button
                          type="button"
                          onClick={handleBack}
                          className="rounded-md h-9 px-3 text-sm font-medium border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200"
                        >
                          {t('common:cancel')}
                        </button>
                      )}
                      <button
                        type="submit"
                        form="profile-form"
                        disabled={saving}
                        className="rounded-md h-9 px-3 text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {success ? t('changesSaved') : t('saveChanges')}
                      </button>
                    </div>
                  </div>

                  {/* Two-Factor Authentication Section - match example Card (MFASettings has its own card) */}
                  <MFASettings />
                </div>

                {/* Right Column - Badges (same layout for all roles: employees see list/empty, managers/admins see empty state) */}
                <div className="lg:col-span-2 lg:row-span-2">
                  <div className="p-6 flex flex-col items-center justify-center bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-600 shadow-sm h-full min-h-[280px]">
                    {badgesLoading ? (
                      <p className="text-sm text-slate-600 dark:text-slate-400">{t('loadingBadges')}</p>
                    ) : badges.length === 0 ? (
                      <>
                        <span className="material-symbols-outlined text-5xl text-slate-300 dark:text-slate-500 mb-4">emoji_events</span>
                        <h3 className="font-semibold text-slate-900 dark:text-slate-100 text-center mb-3">{t('badgesTitle')}</h3>
                        <p className="text-sm text-slate-600 dark:text-slate-400 text-center">{t('noBadges')}</p>
                      </>
                    ) : (
                      <div className="w-full flex flex-col h-full">
                        <div className="flex items-center gap-2 mb-4">
                          <span className="material-symbols-outlined text-2xl text-slate-400 dark:text-slate-500">emoji_events</span>
                          <h3 className="font-semibold text-slate-900 dark:text-slate-100">{t('badgesTitle')}</h3>
                        </div>
                        <div className="flex-1 overflow-y-auto space-y-4">
                          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-100">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-sm text-slate-600 dark:text-slate-400 font-medium">{t('totalPoints')}</p>
                                <p className="text-2xl font-bold text-blue-600 mt-1">
                                  {badges.reduce((sum, badge) => sum + (badge.points_earned || 0), 0)}
                                </p>
                              </div>
                              <span className="material-symbols-outlined text-4xl text-blue-400">stars</span>
                            </div>
                          </div>
                          <div className="space-y-3">
                            {badges.map((badge) => (
                              <div
                                key={badge.id}
                                className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 dark:border-slate-600 hover:border-blue-300 dark:hover:border-blue-600 hover:bg-blue-50/50 dark:hover:bg-blue-900/30 transition-colors"
                              >
                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center shadow overflow-hidden flex-shrink-0">
                                  {badge.badge?.icon ? (
                                    <img
                                      src={getBadgeIconUrl(badge.badge.icon)}
                                      alt={badge.badge.name || t('common:badge')}
                                      className="w-full h-full object-cover"
                                      onError={(e) => {
                                        e.target.src = defaultBadgeIcon;
                                        e.target.className = 'w-6 h-6 opacity-90';
                                      }}
                                    />
                                  ) : (
                                    <img src={defaultBadgeIcon} alt={t('common:badge')} className="w-6 h-6 opacity-90" />
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <h4 className="font-semibold text-slate-900 dark:text-slate-100 truncate text-sm">
                                      {badge.badge?.name || t('common:badge')}
                                    </h4>
                                    {getDisputeStatusBadge(badge)}
                                  </div>
                                  <p className="text-xs text-slate-600 dark:text-slate-400 mt-0.5">
                                    {new Date(badge.awarded_date).toLocaleDateString(getDateLocale(), {
                                      year: 'numeric',
                                      month: 'short',
                                      day: 'numeric'
                                    })}
                                    {badge.team_name && ` · ${badge.team_name}`}
                                  </p>
                                  <div className="flex items-center gap-2 mt-1">
                                    <button
                                      type="button"
                                      onClick={() => handleViewRule(badge)}
                                      className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                                    >
                                      {t('viewRule')}
                                    </button>
                                    {!badge.has_pending_dispute && userData?.role === 'EMPLOYE' && (
                                      <button
                                        type="button"
                                        onClick={() => handleOpenDisputeModal(badge)}
                                        className="text-xs text-red-600 hover:text-red-700 font-medium"
                                      >
                                        {t('reportAnomaly')}
                                      </button>
                                    )}
                                  </div>
                                </div>
                                <div className="text-right flex-shrink-0">
                                  <span className="font-bold text-blue-600">+{badge.points_earned || 0}</span>
                                  <span className="text-xs text-slate-500 dark:text-slate-400 block">{t('common:points')}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </main>
      
      {/* Dispute Modal */}
      <BadgeAwardDisputeModal
        isOpen={disputeModalOpen}
        onClose={handleCloseDisputeModal}
        badgeAward={selectedBadgeAward}
        onSuccess={handleDisputeSuccess}
      />

      {/* Badge Rule Detail Modal */}
      <AnimatePresence>
        {ruleModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto border border-slate-200 dark:border-slate-600"
            >
              {/* Header */}
              <div className="sticky top-0 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-600 px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
                    <span className="material-symbols-outlined text-blue-600 text-2xl">rule</span>
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">{t('badgeRuleDetails')}</h2>
                    <p className="text-sm text-slate-600 dark:text-slate-400">{selectedRule?.name || t('common:loadingEllipsis')}</p>
                  </div>
                </div>
                <button
                  onClick={handleCloseRuleModal}
                  className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                >
                  <span className="material-symbols-outlined text-slate-600 dark:text-slate-400">close</span>
                </button>
              </div>

              {/* Content */}
              <div className="p-6">
                {ruleLoading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                    <p className="text-slate-600 dark:text-slate-400">{t('loadingRuleDetails')}</p>
                  </div>
                ) : selectedRule ? (
                  <div className="space-y-6">
                    {/* Rule Info */}
                    <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-4 border border-slate-200 dark:border-slate-600">
                      <h3 className="font-semibold text-slate-900 dark:text-slate-100 mb-3">{t('ruleInformation')}</h3>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="font-medium text-slate-700 dark:text-slate-300">{t('common:version')}:</span>
                          <span className="ml-2 text-slate-600 dark:text-slate-400">{t('common:versionNumber', { version: selectedRule.version })}</span>
                        </div>
                        <div>
                          <span className="font-medium text-slate-700 dark:text-slate-300">{t('effectiveDate')}:</span>
                          <span className="ml-2 text-slate-600 dark:text-slate-400">
                            {new Date(selectedRule.effective_date).toLocaleDateString(getDateLocale(), {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </span>
                        </div>
                        <div>
                          <span className="font-medium text-slate-700 dark:text-slate-300">{t('badgeType')}:</span>
                          <span className="ml-2 text-slate-600 dark:text-slate-400">{selectedRule.badge_type_display || selectedRule.badge_type}</span>
                        </div>
                        <div>
                          <span className="font-medium text-slate-700 dark:text-slate-300">{t('pointsValue')}:</span>
                          <span className="ml-2 text-slate-600 dark:text-slate-400 font-semibold">{selectedRule.points_value}</span>
                        </div>
                      </div>
                    </div>

                    {/* Calculation Criteria */}
                    <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-4 border border-blue-200 dark:border-blue-700/50">
                      <h3 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                        <span className="material-symbols-outlined text-lg">calculate</span>
                        {t('calculationCriteria')}
                      </h3>
                      <div className="space-y-2 text-sm">
                        <div>
                          <span className="font-medium text-blue-900">{t('minimumOperations')}:</span>
                          <span className="ml-2 text-blue-700">{selectedRule.min_operations || 0}</span>
                        </div>
                        {selectedRule.min_hours != null && selectedRule.min_hours !== '' && (
                          <div>
                            <span className="font-medium text-blue-900">{t('minimumHours')}:</span>
                            <span className="ml-2 text-blue-700">{formatMinHoursHHMM(selectedRule.min_hours)}</span>
                          </div>
                        )}
                        <div>
                          <span className="font-medium text-blue-900">{t('tieHandling')}:</span>
                          <span className="ml-2 text-blue-700">{selectedRule.tie_handling_display || selectedRule.tie_handling}</span>
                        </div>
                        <div>
                          <span className="font-medium text-blue-900">{t('applicableTo')}:</span>
                          <span className="ml-2 text-blue-700">
                            {selectedRule.is_global ? t('allTeams') : t('teamCount', { count: selectedRule.applicable_teams?.length || 0 })}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Description */}
                    {selectedRule.description && (
                      <div>
                        <h3 className="font-semibold text-slate-900 dark:text-slate-100 mb-2">{t('description')}</h3>
                        <p className="text-sm text-slate-600 dark:text-slate-400 bg-slate-50 dark:bg-slate-700/50 p-3 rounded-lg">{selectedRule.description}</p>
                      </div>
                    )}

                    {/* Historical Versions */}
                    {selectedRule.historical_versions && selectedRule.historical_versions.length > 0 && (
                      <div>
                        <h3 className="font-semibold text-slate-900 dark:text-slate-100 mb-3">{t('versionHistory')}</h3>
                        <div className="space-y-2 max-h-60 overflow-y-auto">
                          {selectedRule.historical_versions.slice(0, 5).map((version) => (
                            <div key={version.id} className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg p-3 text-xs">
                              <div className="flex items-center justify-between mb-1">
                                <span className="font-medium text-slate-700 dark:text-slate-300">{version.change_type || t('common:updated')}</span>
                                <span className="text-slate-500 dark:text-slate-400">
                                  {new Date(version.changed_at).toLocaleDateString(getDateLocale(), {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric'
                                  })}
                                </span>
                              </div>
                              {version.changed_by_name && (
                                <p className="text-slate-500 dark:text-slate-400">{t('changedBy', { name: version.changed_by_name })}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                    <span className="material-symbols-outlined text-4xl mb-2 block">error</span>
                    <p>{t('loadRuleDetailsFailed')}</p>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}

        {/* Change Password Modal */}
        <AnimatePresence>
          {changePasswordModalOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={closeChangePasswordModal}>
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.2 }}
                className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-600 shadow-xl max-w-md w-full overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-700 flex items-center justify-between">
                  <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100">{t('changePassword')}</h3>
                  <button
                    type="button"
                    onClick={closeChangePasswordModal}
                    className="p-1 rounded-lg text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                    aria-label={t('common:close')}
                  >
                    <span className="material-symbols-outlined text-[24px]">close</span>
                  </button>
                </div>
                <form onSubmit={handleChangePasswordSubmit} className="p-6 space-y-4">
                  {changePasswordSuccess && (
                    <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700/50 dark:text-emerald-200 px-4 py-3 rounded-lg flex items-center gap-2 text-sm">
                      <span className="material-symbols-outlined text-[20px]">check_circle</span>
                      <span>{t('passwordChanged')}</span>
                    </div>
                  )}
                  {changePasswordGeneralError && (
                    <div className="bg-red-50 border border-red-200 text-red-700 dark:bg-red-900/30 dark:border-red-700/50 dark:text-red-200 px-4 py-3 rounded-lg flex items-center gap-2 text-sm">
                      <span className="material-symbols-outlined text-[20px] shrink-0">error</span>
                      <span>{changePasswordGeneralError}</span>
                    </div>
                  )}
                  <div className="space-y-2">
                    <label className="text-slate-700 dark:text-slate-300 text-sm font-semibold block">{t('currentPassword')}</label>
                    <div className="relative">
                      <input
                        type={showChangePasswordFields.current ? 'text' : 'password'}
                        value={changePasswordForm.currentPassword}
                        onChange={(e) => handleChangePasswordInputChange('currentPassword', e.target.value)}
                        className={`w-full h-10 rounded-lg border px-4 pr-10 text-slate-900 dark:text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary ${
                          getChangePasswordFieldError('old_password') ? 'border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/30' : 'border-slate-200 bg-white dark:border-slate-600 dark:bg-slate-800'
                        }`}
                        placeholder={t('currentPasswordPlaceholder')}
                        disabled={changePasswordSaving}
                        autoComplete="current-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowChangePasswordFields(prev => ({ ...prev, current: !prev.current }))}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300"
                        tabIndex={-1}
                        aria-label={showChangePasswordFields.current ? t('common:hidePassword') : t('common:showPassword')}
                      >
                        <span className="material-symbols-outlined text-[20px]">{showChangePasswordFields.current ? 'visibility_off' : 'visibility'}</span>
                      </button>
                    </div>
                    {getChangePasswordFieldError('old_password') && (
                      <p className="text-red-600 text-xs">{getChangePasswordFieldError('old_password')}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <label className="text-slate-700 dark:text-slate-300 text-sm font-semibold block">{t('newPassword')}</label>
                    <div className="relative">
                      <input
                        type={showChangePasswordFields.new ? 'text' : 'password'}
                        value={changePasswordForm.newPassword}
                        onChange={(e) => handleChangePasswordInputChange('newPassword', e.target.value)}
                        className={`w-full h-10 rounded-lg border px-4 pr-10 text-slate-900 dark:text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary ${
                          getChangePasswordFieldError('new_password') ? 'border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/30' : 'border-slate-200 bg-white dark:border-slate-600 dark:bg-slate-800'
                        }`}
                        placeholder={t('newPasswordPlaceholder')}
                        disabled={changePasswordSaving}
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowChangePasswordFields(prev => ({ ...prev, new: !prev.new }))}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300"
                        tabIndex={-1}
                        aria-label={showChangePasswordFields.new ? t('common:hidePassword') : t('common:showPassword')}
                      >
                        <span className="material-symbols-outlined text-[20px]">{showChangePasswordFields.new ? 'visibility_off' : 'visibility'}</span>
                      </button>
                    </div>
                    {getChangePasswordFieldError('new_password') && (
                      <p className="text-red-600 text-xs">{getChangePasswordFieldError('new_password')}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <label className="text-slate-700 dark:text-slate-300 text-sm font-semibold block">{t('confirmNewPassword')}</label>
                    <div className="relative">
                      <input
                        type={showChangePasswordFields.confirm ? 'text' : 'password'}
                        value={changePasswordForm.confirmPassword}
                        onChange={(e) => handleChangePasswordInputChange('confirmPassword', e.target.value)}
                        className={`w-full h-10 rounded-lg border px-4 pr-10 text-slate-900 dark:text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary ${
                          getChangePasswordFieldError('confirm_password') ? 'border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/30' : 'border-slate-200 bg-white dark:border-slate-600 dark:bg-slate-800'
                        }`}
                        placeholder={t('confirmPasswordPlaceholder')}
                        disabled={changePasswordSaving}
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowChangePasswordFields(prev => ({ ...prev, confirm: !prev.confirm }))}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300"
                        tabIndex={-1}
                        aria-label={showChangePasswordFields.confirm ? t('common:hidePassword') : t('common:showPassword')}
                      >
                        <span className="material-symbols-outlined text-[20px]">{showChangePasswordFields.confirm ? 'visibility_off' : 'visibility'}</span>
                      </button>
                    </div>
                    {getChangePasswordFieldError('confirm_password') && (
                      <p className="text-red-600 text-xs">{getChangePasswordFieldError('confirm_password')}</p>
                    )}
                  </div>
                  <div className="flex justify-end gap-3 pt-2">
                    <button
                      type="button"
                      onClick={closeChangePasswordModal}
                      disabled={changePasswordSaving}
                      className="px-4 py-2 rounded-lg text-slate-700 dark:text-slate-200 text-sm font-semibold border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50"
                    >
                      {t('common:cancel')}
                    </button>
                    <button
                      type="submit"
                      disabled={changePasswordSaving}
                      className="px-4 py-2 rounded-lg bg-primary text-white text-sm font-bold shadow-md hover:bg-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {changePasswordSaving ? (
                        <>
                          <span className="material-symbols-outlined text-[18px] animate-spin">progress_activity</span>
                          <span>{t('common:saving')}</span>
                        </>
                      ) : (
                        <>
                          <span className="material-symbols-outlined text-[18px]">save</span>
                          <span>{t('common:save')}</span>
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </AnimatePresence>
    </div>
  );
};

export default ProfilePage;
